import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export interface BriefingField {
    id: string;
    label: Record<string, string>; // e.g. { en: 'Genre', pt: 'Gênero' }
    type: 'text' | 'textarea' | 'select' | 'checkbox';
    required: boolean;
    options?: Record<string, string>[]; // optional for selects
}

export interface AuteurFormat {
    id: string;
    label: string;
    system_prompt: string;
    output_format: string;
    briefing_flow: BriefingField[];
    thematic_anchors?: {
        a: string;
        b: string;
        c: string;
    };
}

// Built-in formats for auto-seeding
export const DEFAULT_FORMATS: AuteurFormat[] = [
    {
        id: 'ai_film',
        label: 'AI FILM',
        system_prompt: `You are an expert Hollywood Screenwriter and Script Doctor with deep knowledge of narrative theory (McKee, Snyder, Field, Truby).
You will assist the user in writing a Feature Film screenplay.
The user's language is [USER_LANGUAGE].
You must format your responses using standard industry screenplay formatting.
Focus on visual storytelling ("Show, Don't Tell"), distinct character voices, active subtext, and precise cinematic pacing.`,
        output_format: 'master_scene',
        briefing_flow: [
            { id: 'genre', label: { en: 'Genre', pt: 'Gênero', es: 'Género' }, type: 'text', required: true },
            { id: 'logline', label: { en: 'Logline', pt: 'Logline', es: 'Logline' }, type: 'textarea', required: true },
            { id: 'theme', label: { en: 'Core Theme', pt: 'Tema Central', es: 'Tema Central' }, type: 'text', required: false }
        ],
        thematic_anchors: {
            a: "A redenção é possível através do sacrifício.",
            b: "\"No fim, o poder e a sobrevivência justificam os meios.\"",
            c: "A busca pela identidade em um mundo que te ignora."
        }
    },
    {
        id: 'ai_doc',
        label: 'AI DOCUMENTARY',
        system_prompt: `You are an expert Documentary Filmmaker and Scriptwriter specializing in non-fiction structure.
You will assist the user in writing a Documentary script (A/V format or similar).
The user's language is [USER_LANGUAGE].
Focus on factual presentation, investigative angles, compelling interview questions, and clear B-roll visual descriptions synced with audio columns.
Do NOT use fictional narrative tropes. Maintain a journalistic and analytical tone.`,
        output_format: 'dual_column',
        briefing_flow: [
            { id: 'topic', label: { en: 'Main Topic', pt: 'Tópico Principal', es: 'Tema Principal' }, type: 'text', required: true },
            { id: 'angle', label: { en: 'Unique Angle/Thesis', pt: 'Ângulo/Tese', es: 'Ángulo/Tesis' }, type: 'textarea', required: true },
            { id: 'interviewees', label: { en: 'Key Interviewees', pt: 'Entrevistados Principais', es: 'Entrevistados Clave' }, type: 'textarea', required: false },
            { id: 'theme', label: { en: 'Core Theme', pt: 'Tema Central', es: 'Tema Central' }, type: 'text', required: false }
        ],
        thematic_anchors: {
            a: "A verdade histórica como ferramenta de justiça social. / A Força do Relato: A verdade é construída através de entrevistas e experiências humanas diretas.",
            b: "O caos da natureza humana resiste a qualquer explicação simples. / Histórico-Institucional: O foco é a análise de fatos, arquivos e a cronologia dos eventos.",
            c: "Micro-impacto: como pequenos gestos mudam grandes trajetórias. / Tese Investigativa: O objetivo é provar um ponto de vista ou revelar uma faceta oculta da realidade."
        }
    },
    {
        id: 'ai_ad',
        label: 'AI ADVERTISING',
        system_prompt: `You are a Senior Creative Director focused on direct response marketing and brand building. Your task is to write a commercial video script.
The user's language is [USER_LANGUAGE].

1. CREATIVE GUIDELINES (THE GOLDEN RULE):
The creativity of this script MUST NOT come from a disconnected visual spectacle, but from a strong visual metaphor or an expectation break in the first 3 seconds that immediately illustrates the problem or solution.
- PROHIBITED to use industry clichés.
- The product/service must be the facilitator, not the hero. The hero is the transformed consumer.

2. SCRIPT STRUCTURE (Dual-Column Audio/Video format):
Rigorously divide the script into the following phases (based on the requested duration):
- THE HOOK (0-15% of time): Attract attention and qualify the audience immediately. Show the problem in a visceral or intriguing way.
- THE AGITATION AND BRIDGE (15-50% of time): Intensify the pain and introduce the product as the only logical and inevitable solution.
- THE PROOF AND BENEFIT (50-85% of time): Demonstrate the product in action solving the problem. Focus on emotional benefits anchored in technical characteristics.
- THE CALL TO ACTION / CTA (85-100% of time): Clear, imperative instruction with urgency or exclusivity for the next step.

Write the script with specific visual descriptions (VIDEO) and short, natural, persuasive dialogue/voiceover (AUDIO).`,
        output_format: 'dual_column',
        briefing_flow: [
            { id: 'product', label: { en: 'Product/Service', pt: 'Produto/Serviço', es: 'Producto/Servicio' }, type: 'text', required: true },
            { id: 'target_audience', label: { en: 'Target Audience', pt: 'Público-Alvo', es: 'Público Objetivo' }, type: 'text', required: true },
            { id: 'consumer_pain', label: { en: 'Consumer Pain/Problem', pt: 'Problema/Dor do Consumidor', es: 'Dolor del Consumidor' }, type: 'textarea', required: true },
            { id: 'usp', label: { en: 'Unique Selling Proposition', pt: 'Diferencial Único (USP)', es: 'Punto Único (USP)' }, type: 'textarea', required: true },
            { id: 'cta', label: { en: 'Objective (CTA)', pt: 'Objetivo (CTA)', es: 'Objetivo (CTA)' }, type: 'text', required: true },
            { id: 'duration', label: { en: 'Duration (e.g., 30s)', pt: 'Duração (ex: 30s)', es: 'Duración (ej: 30s)' }, type: 'text', required: false }
        ],
        thematic_anchors: {
            a: "A quebra do clichê desde o primeiro segundo. / Hook Metaphor: Comece descartando imediatamente uma convenção batida.",
            b: "O consumidor como herói da própria transformação. / Shift: O produto apenas destrava o que o cliente já precisava ser.",
            c: "Urgência não é gritar, é mostrar raridade. / Action: CTA ancorado em valor imediato, não apenas numa ordem."
        }
    },
    {
        id: 'ai_anim',
        label: 'AI ANIMATION',
        system_prompt: `You are an expert Animation Writer and Storyboard Artist well-versed in the specific mechanics of animated storytelling.
You will assist the user in writing an Animation script.
The user's language is [USER_LANGUAGE].
Focus on exaggerated visuals, extreme physics, character-specific physical comedy/action, and precise animation directions.
Think in terms of distinct frames, key poses, visual gags, and pacing that leverages the medium's limitless potential.`,
        output_format: 'master_scene',
        briefing_flow: [
            { id: 'animation_style', label: { en: 'Animation Style (e.g., 2D, 3D, Stop-Motion)', pt: 'Estilo de Animação', es: 'Estilo de Animación' }, type: 'text', required: true },
            { id: 'logline', label: { en: 'Logline', pt: 'Logline', es: 'Logline' }, type: 'textarea', required: true },
            { id: 'target_age', label: { en: 'Target Age Group', pt: 'Faixa Etária Alvo', es: 'Rango de Edad' }, type: 'text', required: false },
            { id: 'theme', label: { en: 'Core Theme', pt: 'Tema Central', es: 'Tema Central' }, type: 'text', required: false }
        ],
        thematic_anchors: {
            a: "A amizade supera qualquer barreira física ou mágica.",
            b: "O mundo é absurdo e a única resposta é a subversão/humor.",
            c: "O rito de passagem: a coragem de ser diferente da sua espécie."
        }
    }
];

/**
 * Auto-seeds the collection if it's empty.
 */
async function autoSeedFormats(): Promise<AuteurFormat[]> {
    console.log("Auto-seeding auteur_formats...");
    const promises = DEFAULT_FORMATS.map(async (format) => {
        await setDoc(doc(db, 'auteur_formats', format.id), format);
    });
    await Promise.all(promises);
    return DEFAULT_FORMATS;
}

/**
 * Retrieves all available scriptwriting formats.
 * If none exist, auto-seeds the defaults and returns them.
 */
export async function getAuteurFormats(): Promise<AuteurFormat[]> {
    try {
        const formatsSnap = await getDocs(collection(db, 'auteur_formats'));

        if (formatsSnap.empty) {
            return await autoSeedFormats();
        }

        const formats: AuteurFormat[] = [];
        formatsSnap.forEach((docSnap) => {
            formats.push(docSnap.data() as AuteurFormat);
        });

        return formats;
    } catch (error) {
        console.error("Error fetching auteur formats:", error);
        // Fallback to memory defaults to unblock UI if network/emulator fails
        return DEFAULT_FORMATS;
    }
}

/**
 * Retrieves the fully formatted system context and briefing flow for a specific format.
 */
export async function getSystemContext(formatId: string, userLang: string): Promise<{ systemContext: string; briefingFlow: BriefingField[]; outputFormat: string }> {
    let formatData: AuteurFormat | undefined;

    try {
        const formatRef = doc(db, 'auteur_formats', formatId);
        const formatSnap = await getDoc(formatRef);

        if (formatSnap.exists()) {
            formatData = formatSnap.data() as AuteurFormat;
        }
    } catch (error) {
        console.warn(`Firestore read failed for format ${formatId}, falling back to defaults:`, error);
        // Fallback logic continues below
    }

    // If Firestore failed or document doesn't exist, check local memory defaults
    if (!formatData) {
        formatData = DEFAULT_FORMATS.find(f => f.id === formatId) || DEFAULT_FORMATS[0];
    }

    // Process the dynamic variables in the prompt
    let systemContext = formatData.system_prompt;
    systemContext = systemContext.replace('[USER_LANGUAGE]', userLang.toUpperCase());

    return {
        systemContext,
        briefingFlow: formatData.briefing_flow || [],
        outputFormat: formatData.output_format || 'master_scene'
    };
}
